import { useEffect, useState } from "react";
import { BadgeCheck, Newspaper, Leaf, LinkIcon, ExternalLink, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const KIND_LABEL = { news: "News", new_arrival: "New arrival", hunt_report: "Hunt report", event: "Event", sale: "Sale" };

export function StatePartnerContent({ state }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!state) return;
    setData(null);
    api.statePartnerContent(state).then(setData).catch(() => setData(null));
  }, [state]);

  if (!data || !data.partner) return null;
  const p = data.partner;
  const hasContent = data.news.length || data.conservation.length || data.resources.length;
  if (!hasContent) return null;

  return (
    <section className="max-w-[1240px] mx-auto px-5 sm:px-10 py-12" data-testid="state-partner-content">
      <div className="flex items-center gap-3 mb-6">
        {p.logo ? <img src={p.logo} alt={p.name} className="w-11 h-11 rounded-lg object-cover border border-saddle/20" /> : null}
        <div>
          <span className="hr-overline text-blaze">From your state agency</span>
          <h2 className="font-serif text-2xl sm:text-3xl text-charcoal flex items-center gap-2">
            {p.name}
            {(p.verification_status || "").startsWith("verified") && <BadgeCheck size={18} className="text-blaze" />}
          </h2>
        </div>
        <Link to={p.public_path} className="ml-auto text-sm font-semibold text-blaze hover:underline inline-flex items-center gap-1" data-testid="state-partner-profile-link">Visit page <ArrowRight size={14} /></Link>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* News */}
        {data.news.length > 0 && (
          <div className="lg:col-span-2" data-testid="state-partner-news">
            <h3 className="text-[12px] font-bold uppercase tracking-[0.18em] text-forest flex items-center gap-2 mb-3"><Newspaper size={14} /> Latest news</h3>
            <div className="space-y-3">
              {data.news.map((n) => (
                <article key={n.id} className="border border-saddle/20 bg-white rounded-lg p-4 flex gap-4" data-testid={`state-news-${n.id}`}>
                  {n.media && <img src={n.media} alt="" loading="lazy" className="w-20 h-20 object-cover rounded shrink-0" />}
                  <div className="min-w-0">
                    <span className="text-[10px] font-bold uppercase bg-blaze/10 text-blaze px-2 py-0.5 rounded-full">{KIND_LABEL[n.post_kind] || "News"}</span>
                    {n.title && <h4 className="font-serif text-lg text-forest mt-1.5">{n.title}</h4>}
                    {n.text && <p className="text-sm text-charcoal/75 mt-1 leading-relaxed line-clamp-3">{n.text}</p>}
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-8">
          {/* Conservation */}
          {data.conservation.length > 0 && (
            <div data-testid="state-partner-conservation">
              <h3 className="text-[12px] font-bold uppercase tracking-[0.18em] text-forest flex items-center gap-2 mb-3"><Leaf size={14} /> Conservation projects</h3>
              <div className="space-y-2.5">
                {data.conservation.map((c) => (
                  <div key={c.id} className="border border-saddle/20 bg-white rounded-lg p-3" data-testid={`conservation-${c.id}`}>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-forest text-sm">{c.title}</p>
                      {c.status && <span className="text-[10px] font-bold uppercase bg-forest/10 text-forest px-1.5 py-0.5 rounded">{c.status}</span>}
                    </div>
                    {c.description && <p className="text-xs text-charcoal/65 mt-1 leading-relaxed">{c.description}</p>}
                    {c.url && <a href={c.url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-blaze inline-flex items-center gap-1 mt-1">Learn more <ExternalLink size={10} /></a>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Resources */}
          {data.resources.length > 0 && (
            <div data-testid="state-partner-resources">
              <h3 className="text-[12px] font-bold uppercase tracking-[0.18em] text-forest flex items-center gap-2 mb-3"><LinkIcon size={14} /> Resource center</h3>
              <div className="space-y-2">
                {data.resources.map((r) => (
                  <a key={r.id} href={r.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-saddle/20 bg-white rounded-lg p-3 hover:border-forest transition-colors group" data-testid={`resource-${r.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-forest text-sm truncate">{r.title}</p>
                      {r.category && <span className="text-[10px] uppercase text-charcoal/45">{r.category}</span>}
                    </div>
                    <ExternalLink size={14} className="text-charcoal/40 group-hover:text-blaze shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
